import React, { FC, ReactNode } from "react";
import toast from "react-hot-toast";
import { LocationListingModal } from "models/LocationModal";
import { useData } from "data/data-provider";
import RentalCarTimesRangeInput from "../(car-search-form)/RentalCarTimesRangeInput";
import DatesPickerB from "./DatesPickerB";
import LocationPickerB from "./LocationPickerB";
import TimePickerB from "./TimePickerB";

export interface CarTripCardProps {
  ourLocation: LocationListingModal[];
  selectedCar: any;
  /** The drop-off is entered separately (the renter opened it). */
  isDropOffVisible: boolean;
  /** Some branch takes returns, so "Different return location" can be offered. */
  canChooseDropoff: boolean;
  onDifferentDropoff: () => void;
  onSameDropoff: () => void;
  /**
   * The dates field of tenants that offer long-term or hourly rentals: their own tabbed picker. The rest
   * get the calendar of this car's availability (DatesPickerB).
   */
  datesField?: ReactNode;
  /**
   * Keep the times field's effects mounted (the page does; the trip sheet of the scrolled bar does not,
   * its page is still there with them). Mounted as the old page mounted the field: from the first view on
   * phones, once the dates are set on desktop.
   */
  timeEffects?: boolean;
  /** `card`: on the page, with its heading, and the anchor the scrolled bar watches. `sheet`: bare, inside the bar's sheet. */
  variant?: "card" | "sheet";
}

/**
 * 1NOW-11176: the trip fields of the Design B car page as one hairline card of large tappable fields
 * (location, dates, times), the way the trip box of turo.com's listing page lays them out. All of them are on show from the start and each stays locked until the one before it is set (the
 * phone rule from 1NOW-11155, now on every width): dates until a pick-up location, times until both dates.
 * What a tile does is the picker's own; this only lays them out and keeps the order.
 */
const CarTripCard: FC<CarTripCardProps> = ({
  ourLocation,
  selectedCar,
  isDropOffVisible,
  canChooseDropoff,
  onDifferentDropoff,
  onSameDropoff,
  datesField,
  timeEffects = false,
  variant = "card",
}) => {
  const { pickupLocation, startDate, endDate, dropoffLocationType } = useData();
  const datesLocked = !pickupLocation;
  const timesLocked = !startDate || !endDate;

  const locked = (isLocked: boolean, message: string, children: ReactNode) => (
    <div
      className={isLocked ? "opacity-60 [&>*]:pointer-events-none" : undefined}
      onClick={isLocked ? () => toast.error(message) : undefined}
    >
      {children}
    </div>
  );

  const body = (
    <div className="space-y-3">
      <LocationPickerB kind="pickup" ourLocation={ourLocation} separateDropoff={isDropOffVisible} />

      {isDropOffVisible && (
        <div>
          <LocationPickerB kind="dropoff" ourLocation={ourLocation} />
          <button type="button" onClick={onSameDropoff} className="mt-2 text-[13px] font-medium text-text-color underline">
            Same drop-off location
          </button>
        </div>
      )}

      {locked(datesLocked, "Please select pick-up location", datesField ?? <DatesPickerB selectedCar={selectedCar} />)}
      {locked(timesLocked, "Please pick your dates first", <TimePickerB />)}

      {timeEffects && (
        <RentalCarTimesRangeInput
          effectsOnly
          dropOffLocationType={dropoffLocationType}
          className="w-full"
          searchBar={false}
        />
      )}

      {!isDropOffVisible && canChooseDropoff && (
        <button type="button" onClick={onDifferentDropoff} className="text-[13px] font-medium text-text-color underline">
          Different return location
        </button>
      )}
    </div>
  );

  if (variant === "sheet") return body;

  return (
    <section
      data-car-trip-fields
      data-testid="car-trip-card"
      className="cpb rounded-xl border border-text-color/10 bg-card-color p-4 md:p-5"
    >
      <h2 id="trip-details" className="mb-4 text-xl font-semibold leading-tight text-text-color">
        Your trip
      </h2>
      {body}
    </section>
  );
};

export default CarTripCard;
